/* One square per booked service, whichever station puts it up.
 *
 * A salon signs the same station account in on several screens, and that is
 * always going to happen (Kevin, 2026-10-25). Every one of them runs the
 * auto-start: when Mangomint shows a client in service, each screen sees it
 * within the same minute, finds no square for that booking yet, and puts one
 * up. With a fresh random id each, the first to reach the server won and every
 * other screen's square was refused by `tt_turns_appt_service_uniq` — one turn
 * per booked service — and reported as "1 change(s) were refused by the server",
 * a warning about nothing that had gone wrong.
 *
 * So a square for a booked service gets an id worked out from the booking:
 * every screen writes the SAME row, the second write lands on the first as an
 * update instead of being refused, and the board ends up with one square
 * either way. A square with no booking behind it keeps a random id — two of
 * those are two different walk-ins.
 *
 * And when one is refused anyway — a booking attached to an existing square on
 * two screens at once — that is the same race lost, not a write gone missing:
 * `isLostTurnRace` says so, and the station reloads its squares from the
 * server instead of reporting a failure.
 *
 * Pure; imports nothing.
 */

// FNV-1a, 32 bits, from a starting value — four of these make the 128 bits.
function fnv(str, seed) {
  var h = seed >>> 0;
  for (var i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

function hex8(n) { return ('00000000' + (n >>> 0).toString(16)).slice(-8); }

/* A UUID — version 8, the "made by the application" kind — that is always the
 * same for the same salon and booked service, and different for any other. */
export function turnIdForBooking(tenantId, appointmentServiceId) {
  var key = 'tt-turn|' + (tenantId || '') + '|' + appointmentServiceId;
  var hex = hex8(fnv(key, 0x811c9dc5)) + hex8(fnv(key, 0x01000193)) +
            hex8(fnv(key, 0x9e3779b9)) + hex8(fnv(key, 0x85ebca6b));
  // Version nibble 8, variant bits 10.
  var v = '8' + hex.slice(13, 16);
  var r = ((parseInt(hex.charAt(16), 16) & 0x3) | 0x8).toString(16) + hex.slice(17, 20);
  return hex.slice(0, 8) + '-' + hex.slice(8, 12) + '-' + v + '-' + r + '-' + hex.slice(20, 32);
}

/* Whether a refused write is a square another screen already put up for the
 * same booking: the unique index on (tenant, booked service), refused as a
 * duplicate (Postgres 23505). */
export function isLostTurnRace(table, error) {
  if (table !== 'tt_turns' || !error) return false;
  var msg = String(error.message || '') + ' ' + String(error.details || '');
  return error.code === '23505' && msg.indexOf('tt_turns_appt_service_uniq') !== -1;
}
